<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
} = useMessageContext();
const { t } = useI18n();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() => {
  const colorMap = {
    [MESSAGE_VARIANTS.AGENT]: 'text-n-blue-12/80 dark:text-n-slate-10',
    [MESSAGE_VARIANTS.BOT]: 'text-n-iris-12/80 dark:text-n-slate-10',
    [MESSAGE_VARIANTS.TEMPLATE]: 'text-n-iris-12/80 dark:text-n-slate-10',
    [MESSAGE_VARIANTS.USER]: 'text-n-slate-11 dark:text-n-slate-11',
    [MESSAGE_VARIANTS.EMAIL]: 'text-n-slate-11 dark:text-n-slate-11',
    [MESSAGE_VARIANTS.ERROR]: 'text-n-ruby-11 dark:text-n-ruby-11',
    [MESSAGE_VARIANTS.PRIVATE]: 'text-n-amber-12/70 dark:text-n-amber-11',
  };

  return colorMap[variant.value] || 'text-n-slate-11 dark:text-n-slate-10';
});

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]:
    'border border-n-blue-7/20 bg-n-solid-blue-2 text-n-blue-12 shadow-[0_10px_24px_rgba(5,150,105,0.08)] ring-1 ring-inset ring-white/40 dark:border-n-blue-8/25 dark:bg-[rgb(6_52_35_/_0.86)] dark:text-n-slate-12 dark:ring-white/5',
  [MESSAGE_VARIANTS.PRIVATE]:
    'border border-n-amber-8/20 bg-n-solid-amber text-n-amber-12 shadow-sm ring-1 ring-inset ring-white/35 dark:border-n-amber-8/25 dark:bg-[rgb(48_32_8_/_0.92)] dark:text-n-amber-12 dark:ring-white/5 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]:
    'border border-n-slate-5/75 bg-[linear-gradient(180deg,rgba(249,251,252,0.98)_0%,rgba(242,247,250,0.98)_100%)] text-n-slate-12 shadow-[0_10px_24px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-white/90 dark:border-n-slate-7/45 dark:bg-[rgb(17_25_37_/_0.96)] dark:text-n-slate-12 dark:ring-white/5 dark:shadow-[0_16px_36px_rgba(0,0,0,0.22)]',
  [MESSAGE_VARIANTS.ACTIVITY]:
    'bg-n-alpha-2 text-n-slate-11 text-sm ring-1 ring-inset ring-n-weak',
  [MESSAGE_VARIANTS.BOT]:
    'border border-n-iris-7/20 bg-n-solid-iris text-n-iris-12 shadow-[0_10px_24px_rgba(22,163,74,0.07)] ring-1 ring-inset ring-white/35 dark:border-n-iris-8/24 dark:bg-[rgb(8_45_33_/_0.88)] dark:text-n-iris-12 dark:ring-white/5',
  [MESSAGE_VARIANTS.TEMPLATE]:
    'border border-n-iris-7/20 bg-n-solid-iris text-n-iris-12 shadow-[0_10px_24px_rgba(22,163,74,0.07)] ring-1 ring-inset ring-white/35 dark:border-n-iris-8/24 dark:bg-[rgb(8_45_33_/_0.88)] dark:text-n-iris-12 dark:ring-white/5',
  [MESSAGE_VARIANTS.ERROR]:
    'border border-n-ruby-8/20 bg-n-ruby-4 text-n-ruby-12 shadow-sm ring-1 ring-inset ring-white/35',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyPreviewClass = computed(() => {
  const map = {
    [MESSAGE_VARIANTS.AGENT]:
      'border border-n-blue-7/18 bg-white/60 text-n-blue-12 dark:border-n-blue-8/24 dark:bg-white/5 dark:text-n-slate-12',
    [MESSAGE_VARIANTS.BOT]:
      'border border-n-iris-7/18 bg-white/55 text-n-iris-12 dark:border-n-iris-8/24 dark:bg-white/5 dark:text-n-slate-12',
    [MESSAGE_VARIANTS.TEMPLATE]:
      'border border-n-iris-7/18 bg-white/55 text-n-iris-12 dark:border-n-iris-8/24 dark:bg-white/5 dark:text-n-slate-12',
    [MESSAGE_VARIANTS.PRIVATE]:
      'border border-n-amber-8/18 bg-white/45 text-n-amber-12 dark:border-n-amber-8/24 dark:bg-white/5 dark:text-n-amber-12',
    [MESSAGE_VARIANTS.ERROR]:
      'border border-n-ruby-8/18 bg-white/55 text-n-ruby-12 dark:border-n-ruby-8/24 dark:bg-white/5 dark:text-n-ruby-12',
    [MESSAGE_VARIANTS.USER]:
      'border border-n-slate-5/65 bg-white/78 text-n-slate-12 dark:border-n-slate-7/35 dark:bg-white/5 dark:text-n-slate-12',
    [MESSAGE_VARIANTS.EMAIL]:
      'border border-n-strong/12 bg-n-alpha-2 text-n-slate-12 dark:border-n-slate-7/35 dark:bg-white/5 dark:text-n-slate-12',
  };

  return (
    map[variant.value] ||
    'border border-n-strong/12 bg-n-alpha-2 text-n-slate-12 dark:border-n-slate-7/35 dark:bg-white/5 dark:text-n-slate-12'
  );
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="mb-2 -mx-1 cursor-pointer rounded-lg p-2 transition-colors hover:brightness-[0.98] dark:hover:brightness-110"
      :class="replyPreviewClass"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <div
            class="chatmigo-message-meta-shell flex"
            :class="flexOrientationClass"
          >
            <MessageMeta
              class="chatmigo-message-meta"
              :class="[emailMetaClass, metaColorClass]"
            />
          </div>
        </template>
      </CaptainGenerationDetails>
      <div
        v-else
        class="chatmigo-message-meta-shell mt-2 flex"
        :class="flexOrientationClass"
      >
        <MessageMeta
          class="chatmigo-message-meta"
          :class="[emailMetaClass, metaColorClass]"
        />
      </div>
    </template>
  </div>
</template>
